import { css, keyframes } from "dx-styles"
import type { FC } from "react"

const sweep = keyframes({
  "0%, 100%": { transform: "rotate(0deg) translateX(0)" },
  "35%": { transform: "rotate(-8deg) translateX(-6px)" },
  "70%": { transform: "rotate(5deg) translateX(4px)" },
})

const swish = keyframes({
  "0%, 100%": { transform: "rotate(0deg)" },
  "50%": { transform: "rotate(-6deg)" },
})

const twitch = keyframes({
  "0%, 88%, 100%": { transform: "rotate(0deg)" },
  "92%": { transform: "rotate(9deg)" },
  "96%": { transform: "rotate(-3deg)" },
})

const peer = keyframes({
  "0%, 100%": { transform: "translate(0, 0)" },
  "35%": { transform: "translate(-2px, 0)" },
  "70%": { transform: "translate(1.5px, 0)" },
})

const bob = keyframes({
  "0%, 100%": { transform: "translateY(0)", opacity: 0.9 },
  "50%": { transform: "translateY(-5px)", opacity: 0.55 },
})

const fox = css({
  position: "relative",
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "visible",
})

const tail = css({
  transformBox: "fill-box",
  transformOrigin: "right bottom",
  animation: `${swish} 5.4s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const ear = css({
  transformBox: "fill-box",
  transformOrigin: "left bottom",
  animation: `${twitch} 6.8s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const magnifier = css({
  transformBox: "view-box",
  transformOrigin: "150px 168px",
  animation: `${sweep} 4.8s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const pupils = css({
  animation: `${peer} 4.8s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const query = css({
  animation: `${bob} 3.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

export const FoxIllustration: FC = () => (
  <svg
    viewBox="0 0 220 220"
    className={fox}
    aria-hidden="true"
  >
    <defs>
      <linearGradient
        id="dovetailFoxFur"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop
          offset="0%"
          stopColor="#F2A05A"
        />
        <stop
          offset="55%"
          stopColor="#E07A3A"
        />
        <stop
          offset="100%"
          stopColor="#B4521F"
        />
      </linearGradient>
      <linearGradient
        id="dovetailFoxCream"
        x1="0"
        y1="0"
        x2="0"
        y2="1"
      >
        <stop
          offset="0%"
          stopColor="#FBF4EA"
        />
        <stop
          offset="100%"
          stopColor="#EBDCC8"
        />
      </linearGradient>
    </defs>

    <ellipse
      cx="112"
      cy="196"
      rx="62"
      ry="8"
      fill="rgba(70, 40, 83, 0.12)"
    />

    <g className={tail}>
      <path
        d="M76 184 C30 190 14 150 32 124 C40 112 56 118 52 132 C46 152 60 170 84 172 Z"
        fill="url(#dovetailFoxFur)"
      />
      <path
        d="M32 124 C40 112 56 118 52 132 C45 131 37 129 32 124 Z"
        fill="url(#dovetailFoxCream)"
      />
    </g>

    <path
      d="M100 104 C130 104 146 130 146 156 C146 180 128 192 100 192 C72 192 56 180 56 156 C56 130 70 104 100 104 Z"
      fill="url(#dovetailFoxFur)"
    />
    <ellipse
      cx="104"
      cy="158"
      rx="22"
      ry="27"
      fill="url(#dovetailFoxCream)"
    />

    <ellipse
      cx="82"
      cy="190"
      rx="13"
      ry="7"
      fill="#4E2A1A"
    />
    <ellipse
      cx="120"
      cy="190"
      rx="13"
      ry="7"
      fill="#4E2A1A"
    />

    <path
      d="M68 72 L70 30 L96 54 Z"
      fill="url(#dovetailFoxFur)"
    />
    <path
      d="M73 62 L74 42 L88 55 Z"
      fill="#4E2A1A"
    />
    <g className={ear}>
      <path
        d="M118 52 L144 28 L148 70 Z"
        fill="url(#dovetailFoxFur)"
      />
      <path
        d="M126 54 L141 40 L142 62 Z"
        fill="#4E2A1A"
      />
    </g>

    <circle
      cx="106"
      cy="84"
      r="38"
      fill="url(#dovetailFoxFur)"
    />
    <path
      d="M74 96 C84 120 122 126 148 108 C140 96 120 92 106 96 C96 99 84 99 74 96 Z"
      fill="url(#dovetailFoxCream)"
    />
    <circle
      cx="147"
      cy="106"
      r="5.5"
      fill="#3A2145"
    />
    <path
      d="M128 114 C133 117 139 116 143 112"
      stroke="#3A2145"
      strokeWidth="2"
      strokeLinecap="round"
      fill="none"
    />

    <circle
      cx="96"
      cy="80"
      r="8"
      fill="#FFFFFF"
    />
    <circle
      cx="124"
      cy="78"
      r="8"
      fill="#FFFFFF"
    />
    <g className={pupils}>
      <circle
        cx="99"
        cy="83"
        r="4.5"
        fill="#0C1436"
      />
      <circle
        cx="97.5"
        cy="81.5"
        r="1.5"
        fill="#FFFFFF"
      />
      <circle
        cx="127"
        cy="81"
        r="4.5"
        fill="#0C1436"
      />
      <circle
        cx="125.5"
        cy="79.5"
        r="1.5"
        fill="#FFFFFF"
      />
    </g>
    <path
      d="M87 68 L104 71 M117 70 L133 65"
      stroke="#4E2A1A"
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />

    <ellipse
      cx="64"
      cy="160"
      rx="12"
      ry="9"
      fill="#4E2A1A"
    />

    <g className={magnifier}>
      <path
        d="M150 168 L166 150"
        stroke="#4E382A"
        strokeWidth="8"
        strokeLinecap="round"
      />
      <circle
        cx="178"
        cy="136"
        r="20"
        fill="rgba(255, 255, 255, 0.4)"
        stroke="#3A2145"
        strokeWidth="5"
      />
      <rect
        x="167"
        y="127"
        width="22"
        height="17"
        rx="3"
        fill="none"
        stroke="#7E4797"
        strokeWidth="2"
        strokeDasharray="3 3"
      />
      <circle
        cx="173"
        cy="134"
        r="2.5"
        fill="none"
        stroke="#7E4797"
        strokeWidth="1.5"
        strokeDasharray="2 2"
      />
      <path
        d="M178 132 L185 132 M178 137 L183 137"
        stroke="#7E4797"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="2 2"
      />
      <path
        d="M166 126 A14 14 0 0 1 176 120"
        stroke="rgba(255, 255, 255, 0.85)"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <ellipse
        cx="152"
        cy="166"
        rx="11"
        ry="9"
        fill="#4E2A1A"
      />
    </g>

    <g className={query}>
      <path
        d="M178 58 C178 47 196 47 196 58 C196 66 187 67 187 76"
        stroke="#FF375B"
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle
        cx="187"
        cy="86"
        r="3"
        fill="#FF375B"
      />
    </g>
  </svg>
)
